import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/admin-layout';
import {
  Card,
  Table,
  Th,
  Td,
  Button,
  Input,
  Select,
  Modal,
  Badge,
  Pagination,
  Alert,
} from '../components/ui';
import {
  getPeriodeHonorList,
  createPeriodeHonor,
  updatePeriodeHonor,
  deletePeriodeHonor,
  lockPeriodeHonor,
  unlockPeriodeHonor,
  generateDraftHonor,
} from '../features/honorarium/api';
import { RoleCode, type PeriodeHonor, type StatusPeriodeHonor } from '@app/shared';
import { useAuth } from '../features/auth/use-auth';

const BULAN = [
  'Januari',
  'Februari',
  'Maret',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Agustus',
  'September',
  'Oktober',
  'November',
  'Desember',
];

const STATUS_MAP: Record<
  StatusPeriodeHonor,
  { label: string; color: 'green' | 'gray' | 'blue' | 'red' }
> = {
  draft: { label: 'Draft', color: 'gray' },
  diproses: { label: 'Diproses', color: 'blue' },
  terkunci: { label: 'Terkunci', color: 'red' },
};

type FormState = {
  id?: number;
  nama: string;
  bulan: number;
  tahun: number;
};

export function HonorariumPage() {
  const { user } = useAuth();
  const canManage = user?.roles.includes(RoleCode.ADMIN) ?? false;
  const [rows, setRows] = useState<PeriodeHonor[]>([]);
  const [meta, setMeta] = useState({ page: 1, limit: 10, total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<'' | StatusPeriodeHonor>('');
  const [modalOpen, setModalOpen] = useState(false);
  const [detail, setDetail] = useState<PeriodeHonor | null>(null);
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>({
    nama: '',
    bulan: new Date().getMonth() + 1,
    tahun: new Date().getFullYear(),
  });
  const [formError, setFormError] = useState<string | null>(null);

  const load = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getPeriodeHonorList({
        page,
        limit: 10,
        search: search || undefined,
        status: filterStatus || undefined,
      });
      setRows(result.rows);
      setMeta(result.meta);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
  }, [search, filterStatus]);

  const openCreate = () => {
    setForm({
      nama: '',
      bulan: new Date().getMonth() + 1,
      tahun: new Date().getFullYear(),
    });
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (row: PeriodeHonor) => {
    setForm({ id: row.id, nama: row.nama, bulan: row.bulan, tahun: row.tahun });
    setFormError(null);
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!form.nama.trim()) {
      setFormError('Nama periode wajib diisi');
      return;
    }
    try {
      if (form.id) {
        await updatePeriodeHonor(form.id, {
          nama: form.nama.trim(),
          bulan: form.bulan,
          tahun: form.tahun,
        });
        setSuccess('Periode honor berhasil diperbarui');
      } else {
        await createPeriodeHonor({
          nama: form.nama.trim(),
          bulan: form.bulan,
          tahun: form.tahun,
        });
        setSuccess('Periode honor berhasil dibuat');
      }
      setModalOpen(false);
      load(meta.page);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Gagal menyimpan');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Yakin ingin menghapus periode honor ini?')) return;
    try {
      await deletePeriodeHonor(id);
      setSuccess('Periode honor berhasil dihapus');
      load(meta.page);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal menghapus');
    }
  };

  const handleLock = async (id: number) => {
    if (
      !window.confirm(
        'Kunci periode ini? Periode terkunci tidak dapat diedit, dihapus, atau di-generate ulang.',
      )
    )
      return;
    try {
      await lockPeriodeHonor(id);
      setSuccess('Periode honor berhasil dikunci');
      load(meta.page);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal mengunci periode');
    }
  };

  const handleUnlock = async (id: number) => {
    if (!window.confirm('Buka kembali periode ini? Status akan kembali menjadi Draft.')) return;
    try {
      await unlockPeriodeHonor(id);
      setSuccess('Periode honor berhasil dibuka kembali');
      load(meta.page);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal membuka periode');
    }
  };

  const handleGenerateDraft = async (id: number, kodePeriode: string) => {
    if (
      !window.confirm(
        `Generate draft honor untuk periode ${kodePeriode}? Ini akan membuat draft honor untuk semua pegawai aktif.`,
      )
    )
      return;
    setError(null);
    setSuccess(null);
    try {
      const result = await generateDraftHonor(id);
      setSuccess(`Draft honor berhasil dibuat untuk ${result.created} pegawai`);
      load(meta.page);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Gagal generate draft honor';
      setError(message);
    }
  };

  const fmtDateTime = (v: string | null | undefined) => {
    if (!v) return '-';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return v;
    return d.toLocaleString('id-ID');
  };

  return (
    <AdminLayout title="Honorarium">
      {error && <Alert type="error">{error}</Alert>}
      {success && <Alert type="success">{success}</Alert>}
      <Card>
        <div
          style={{
            display: 'flex',
            gap: '1rem',
            marginBottom: '1rem',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
          }}
        >
          <Button variant="secondary" onClick={() => navigate('/honorarium/slip')}>
            Slip Honorarium
          </Button>
          <Input
            placeholder="Cari periode honor..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: '200px' }}
          />
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as '' | StatusPeriodeHonor)}
            style={{ minWidth: '160px' }}
          >
            <option value="">Semua Status</option>
            <option value="draft">Draft</option>
            <option value="diproses">Diproses</option>
            <option value="terkunci">Terkunci</option>
          </Select>
          {canManage && <Button onClick={openCreate}>Tambah Periode</Button>}
        </div>
        <Table>
          <thead>
            <tr>
              <Th>Kode</Th>
              <Th>Nama</Th>
              <Th>Bulan</Th>
              <Th>Tahun</Th>
              <Th>Status</Th>
              {canManage && <Th style={{ textAlign: 'right' }}>Aksi</Th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const locked = row.status === 'terkunci';
              return (
                <tr key={row.id}>
                  <Td>{row.kode_periode}</Td>
                  <Td>{row.nama}</Td>
                  <Td>{BULAN[row.bulan - 1] ?? row.bulan}</Td>
                  <Td>{row.tahun}</Td>
                  <Td>
                    <Badge color={STATUS_MAP[row.status]?.color ?? 'gray'}>
                      {STATUS_MAP[row.status]?.label ?? row.status}
                    </Badge>
                  </Td>
                  {canManage && (
                    <Td style={{ textAlign: 'right' }}>
                      <Button variant="secondary" onClick={() => setDetail(row)}>
                        Detail
                      </Button>{' '}
                      {!locked && (
                        <>
                          <Button onClick={() => handleGenerateDraft(row.id, row.kode_periode)}>
                            Generate Draft
                          </Button>{' '}
                          <Button variant="secondary" onClick={() => openEdit(row)}>
                            Ubah
                          </Button>{' '}
                          <Button variant="danger" onClick={() => handleDelete(row.id)}>
                            Hapus
                          </Button>{' '}
                          <Button variant="secondary" onClick={() => handleLock(row.id)}>
                            Kunci
                          </Button>
                        </>
                      )}
                      {locked && (
                        <Button variant="secondary" onClick={() => handleUnlock(row.id)}>
                          Buka Kunci
                        </Button>
                      )}
                    </Td>
                  )}
                </tr>
              );
            })}
            {!loading && rows.length === 0 && (
              <tr>
                <Td colSpan={canManage ? 6 : 5} style={{ textAlign: 'center' }}>
                  Tidak ada data
                </Td>
              </tr>
            )}
          </tbody>
        </Table>
        {loading && <p style={{ textAlign: 'center', marginTop: '1rem' }}>Memuat...</p>}
        <Pagination page={meta.page} totalPages={meta.total_pages} onChange={load} />
      </Card>

      <Modal
        open={modalOpen && canManage}
        onClose={() => setModalOpen(false)}
        title={form.id ? 'Ubah Periode Honor' : 'Tambah Periode Honor'}
      >
        <form onSubmit={handleSubmit}>
          {formError && <Alert type="error">{formError}</Alert>}
          <Input
            label="Nama Periode"
            value={form.nama}
            onChange={(e) => setForm({ ...form, nama: e.target.value })}
            required
          />
          <Select
            label="Bulan"
            value={String(form.bulan)}
            onChange={(e) => setForm({ ...form, bulan: Number(e.target.value) })}
          >
            {BULAN.map((b, i) => (
              <option key={i} value={i + 1}>
                {b}
              </option>
            ))}
          </Select>
          <Input
            label="Tahun"
            type="number"
            value={String(form.tahun)}
            onChange={(e) => setForm({ ...form, tahun: Number(e.target.value) })}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.5rem',
              marginTop: '1rem',
            }}
          >
            <Button variant="secondary" onClick={() => setModalOpen(false)} type="button">
              Batal
            </Button>
            <Button type="submit">Simpan</Button>
          </div>
        </form>
      </Modal>

      <Modal open={detail !== null} onClose={() => setDetail(null)} title="Detail Periode Honor">
        {detail && (
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            <div>
              <strong>Kode:</strong> {detail.kode_periode}
            </div>
            <div>
              <strong>Nama:</strong> {detail.nama}
            </div>
            <div>
              <strong>Bulan:</strong> {BULAN[detail.bulan - 1] ?? detail.bulan}
            </div>
            <div>
              <strong>Tahun:</strong> {detail.tahun}
            </div>
            <div>
              <strong>Status:</strong>{' '}
              <Badge color={STATUS_MAP[detail.status]?.color ?? 'gray'}>
                {STATUS_MAP[detail.status]?.label ?? detail.status}
              </Badge>
            </div>
            <div>
              <strong>Dikunci Pada:</strong> {fmtDateTime(detail.dikunci_at)}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
              <Button variant="secondary" onClick={() => setDetail(null)}>
                Tutup
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </AdminLayout>
  );
}
